import { TableWithLayoutSettings } from '@Components/Tables/TableWithLayoutSettings';
import { type EnumUnit, getEnumUnitLabel, ApiOrderPosition, ApiWeighingSlip } from '@Model';
import { EnumWeighing } from '@Hooks/Orders';

import { ExpandedWeighingSlipSelection } from '@Modules/Orders';
import { Button, type Column, Modal } from '@synqo/frontend-component-library';
import { EnumFrontendLayoutSetting } from '@Model';
import { useCallback, useMemo } from 'react';

export interface WeighingSlipSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderPositions: ApiOrderPosition[];
    onSelect: (orderPositionId: string, weighingSlipId: string | undefined) => boolean;
    weighInNewPosition: () => void;
    mode: EnumWeighing;
}

function weighingSlipHasEmptyWeight(weighingSlip: ApiWeighingSlip, mode: EnumWeighing) {
    if (mode === EnumWeighing.Tare) {
        return weighingSlip.tare_weight === 0;
    } else {
        return weighingSlip.gross_weight === 0;
    }
}

const orderPositionColumns: Column<ApiOrderPosition>[] = [
    {
        name: 'number',
        label: 'WS Nr.',
    },
    {
        name: 'service.display_string',
        label: 'Leistung',
    },
    {
        name: 'quantity',
        label: 'Menge',
        type: 'decimal',
        decimalPlaces: (rowData) => rowData.service.decimal_places_amount,
        size: 150,
    },
    {
        name: 'service.unit',
        label: 'MBez',
        renderCell: ({ cellData }) => getEnumUnitLabel(cellData as EnumUnit),
        size: 150,
    },
    {
        name: 'weight',
        label: 'Gewicht in kg',
        type: 'decimal',
    },
    {
        name: 'price_per_unit',
        label: 'Einzelpreis',
        type: 'currency',
        decimalPlaces: (rowData) => rowData.service.decimal_places_price,
        size: 200,
    },
];

export function WeighingSlipSelectionModal(props: WeighingSlipSelectionModalProps) {
    const { mode, orderPositions, isOpen, onSelect } = props;

    const selectableOrderPositions: ApiOrderPosition[] = useMemo(
        () =>
            orderPositions
                .filter(
                    (pos) =>
                        (mode === EnumWeighing.Gross && pos.weighing_slips.length === 0) ||
                        pos.weighing_slips.some((ws) => weighingSlipHasEmptyWeight(ws, mode)),
                )
                .map((pos) => ({
                    ...pos,
                    weighing_slips: pos.weighing_slips.filter((ws) => weighingSlipHasEmptyWeight(ws, mode)),
                })),
        [mode, orderPositions],
    );

    const onSelectPosition = useCallback(
        (pos: ApiOrderPosition) => {
            if (pos.weighing_slips.length === 0) {
                onSelect(pos.id, undefined);
            } else if (pos.weighing_slips.length === 1) {
                onSelect(pos.id, pos.weighing_slips[0].id);
            }
            return true;
        },
        [onSelect],
    );

    const toolbarElements = useMemo(
        () =>
            mode === EnumWeighing.Gross
                ? [
                      <Button
                          key={'weigh_in_new_position_button'}
                          onClick={props.weighInNewPosition}
                          appearance={'outlined'}
                      >
                          Neue Position einwiegen
                      </Button>,
                  ]
                : undefined,
        [mode, props.weighInNewPosition],
    );

    return (
        <Modal isOpen={isOpen} onClose={props.onClose} title={'Wiegeschein für Auswiegung auswählen'}>
            <TableWithLayoutSettings
                layoutSettingsContext={EnumFrontendLayoutSetting.WeighingPositionsTableLayout}
                columns={orderPositionColumns}
                items={selectableOrderPositions}
                name={'weighingSlipsSelectionTable'}
                renderSubComponent={(pos) =>
                    pos.weighing_slips.length > 0 ? (
                        <ExpandedWeighingSlipSelection onSelect={props.onSelect} position={pos} />
                    ) : undefined
                }
                disableExpanding={(pos) => pos?.weighing_slips?.length < 2}
                onSelect={onSelectPosition}
                toolbarElements={toolbarElements}
            />
        </Modal>
    );
}
